import ClassNames from "classnames";
import { FunctionComponent, useEffect, useRef, useState } from "react";
import isURL from "validator/lib/isURL.js";
import { getPrimaryDomain, selectActiveSite } from "../../selectors/sites.js";
import { injectScript } from "../../slices/loadStates.js";
import { useAppDispatch, useAppSelector } from "../../store/hooks.js";
import moduleBaseStyles from "../../stylesheets/components/module-base.module.css";
import styles from "../../stylesheets/components/video-module.module.css";
import {
  BaseModuleProps,
  VideoModuleSettings,
  WindowState,
} from "../../types/index.js";
import { useDidMountEffect } from "../../utils/hooks.js";
import {
  convertTimeToSeconds,
  getActiveColorScheme,
  getYouTubeVideoId,
} from "../../utils/utils.js";
import Icon from "../Icon.js";
import LazyloadWrapper from "../LazyloadWrapper.js";
import ModuleHeadings from "../ModuleHeadings.js";
import ModuleWithHeadings from "../ModuleWithHeadings.js";
import UsercentricsCMPWrapper from "../UsercentricsCMPWrapper.js";

const youTubeScriptUrl = "https://www.youtube.com/iframe_api";

interface Props extends BaseModuleProps<VideoModuleSettings> {}

type YouTubeSettings = Omit<VideoModuleSettings["global"], "textAlign">;

const VideoModule: FunctionComponent<Props> = ({
  translatedModule: {
    id,
    settings,
    settings: { textAlign, ...youTubeSettings },
    translation: {
      languageId,
      settings: { title, subtitle },
    },
  },
  translatedModule,
  isActive,
  isPreview,
  isFirstOnPage,
}) => {
  const dispatch = useAppDispatch();
  const site = useAppSelector(selectActiveSite);
  const scheme = useAppSelector((state) =>
    getActiveColorScheme(state.colorSchemes, site, translatedModule),
  );
  const domain = getPrimaryDomain(site);
  const loadStatus = useAppSelector(
    (state) => state.loadStates.scripts[youTubeScriptUrl] ?? "unloaded",
  );
  const iFrameRef = useRef<HTMLDivElement>(null);
  const playerRef = useRef<YT.Player>(undefined);
  const youTubeSettingsRef = useRef<YouTubeSettings>(youTubeSettings);
  youTubeSettingsRef.current = youTubeSettings;
  const isDestroyedRef = useRef(false);
  const hasTitleOrSubtitle = Boolean(title || subtitle);
  // Stable key for detecting setting changes in useDidMountEffect.
  // JSON.stringify on every render is negligible for small objects like this.
  const settingsKey = JSON.stringify(youTubeSettings) + hasTitleOrSubtitle;

  const [isLazyloaded, setIsLazyloaded] = useState(false);
  const [consentIsGiven, setConsentIsGiven] = useState(isPreview);

  const initVideo = (settings: YouTubeSettings) => {
    const windowState = window as unknown as WindowState;
    if (!iFrameRef.current || !windowState.YT?.Player || !settings.videoUrl)
      return;

    const { videoUrl, startAt, showInfo, controls, autoPlay, mute } = settings;

    const videoId = getYouTubeVideoId(videoUrl);
    const list = isURL(videoUrl)
      ? (new URL(videoUrl).searchParams.get("list") ?? undefined)
      : undefined;

    isDestroyedRef.current = false;

    playerRef.current = new windowState.YT.Player(iFrameRef.current, {
      videoId,
      height: 390,
      width: 640,
      playerVars: {
        listType: list ? "playlist" : undefined,
        list,
        start: convertTimeToSeconds(startAt),
        modestbranding: 1,
        showinfo: +showInfo,
        controls: +controls,
        rel: 0,
        iv_load_policy: 3,
        // TODO: still throws an error about invalid origin
        origin: `https://${domain}`,
      },
      events: {
        onReady: () => {
          mute && playerRef.current?.mute();
          autoPlay && playerRef.current?.playVideo();
        },
      },
    });
  };

  // Youtube API takes some time after script loading to be available
  const isApiReady = () => {
    const windowState = window as unknown as WindowState;

    const interval = setInterval(() => {
      if (windowState.YT?.Player) {
        clearInterval(interval);
        initVideo(youTubeSettingsRef.current);
      }
    }, 100);
  };

  const loadYouTube = () => {
    dispatch(injectScript(youTubeScriptUrl));
    loadStatus === "loaded" && isApiReady();
  };

  useEffect(() => {
    isPreview && isActive && loadYouTube();
  }, [isPreview, isActive]);

  useEffect(() => {
    !isPreview && !isActive && consentIsGiven && isLazyloaded && loadYouTube();
  }, [isPreview, isActive, isLazyloaded, consentIsGiven]);

  // On unmount
  useEffect(
    () => () => {
      !isDestroyedRef.current && playerRef.current?.destroy();
    },
    [],
  );

  useEffect(() => {
    loadStatus === "loaded" && isApiReady();
  }, [loadStatus]);

  useDidMountEffect(() => {
    if (!isDestroyedRef.current) {
      playerRef.current?.destroy();
      isDestroyedRef.current = true;
    }
    initVideo(youTubeSettingsRef.current);
  }, [settingsKey]);

  return (
    <ModuleWithHeadings
      title={title}
      subtitle={subtitle}
      id={id}
      colors={{ background: scheme.main.separator }}
    >
      <div
        className={ClassNames(
          styles.VideoModule__Wrapper,
          moduleBaseStyles.Module__Wrapper,
          {
            [styles["VideoModule__Wrapper--small"]]: settings.size === "small",
            [styles["VideoModule__Wrapper--full-width"]]:
              settings.size === "fullWidth",
            [styles["VideoModule__Wrapper--no-click"]]: !isActive && isPreview,
          },
        )}
      >
        <div className={moduleBaseStyles.Module__Wrapper}>
          <ModuleHeadings
            className={styles.VideoModule__Title}
            scheme={scheme}
            isFirstOnPage={isFirstOnPage}
            textAlign={textAlign}
            title={title}
            subtitle={subtitle}
          />
        </div>

        <LazyloadWrapper onLoad={setIsLazyloaded}>
          <UsercentricsCMPWrapper
            languageId={languageId}
            serviceName="YouTube Video"
            onConsentGiven={() => setConsentIsGiven(true)}
            consentIsGiven={consentIsGiven}
          >
            <div className={styles.VideoModule__Wrapper__Inner}>
              <div ref={iFrameRef} className={styles.VideoModule__Video}>
                <div className={styles.VideoModule__Placeholder}>
                  <Icon
                    className={styles.VideoModule__PlaceholderIcon}
                    glyph="video"
                  />
                </div>
              </div>
            </div>
          </UsercentricsCMPWrapper>
        </LazyloadWrapper>
      </div>
    </ModuleWithHeadings>
  );
};

export default VideoModule;
